import { RootState } from 'app/store';

export const DEFAULT_RETURN_AIR_TEMPERATURE_COOLING = 75;
export const DEFAULT_RETURN_AIR_TEMPERATURE_HEATING = 70;

// Where the home is, how its occupants set the thermostat, and the block
// loads IF a sizing calculation produced any. Deliberately no fallback for a
// project without one: the server resolves the load from the tonnage seed,
// applying the regional heating factor a client-side guess would miss, and
// then runs both the cost bins and the coverage on that single figure. Which
// calculator produced the loads is not sent either: the server reads it off
// the structure the calculator persisted them on.
export type DuctedCoolingOperatingCostParams = {
  zipCode: string | null;
  totalCoolingBTU: number | null;
  returnAirTemperatureCooling: number;
  totalHeatingBTU: number | null;
  returnAirTemperatureHeating: number;
};

export function ductedCoolingOperatingCostParamsFromProject(
  project: Pick<
    RootState['project'],
    'locationData' | 'totalBTUs' | 'projectSettings'
  >
): DuctedCoolingOperatingCostParams {
  const zipCode = project.locationData?.zipcode?.trim() || null;
  const cooling = project.totalBTUs?.cooling;
  const totalCoolingBTU =
    typeof cooling === 'number' && Number.isFinite(cooling) && cooling > 0
      ? cooling
      : null;
  const heating = project.totalBTUs?.heating;
  const totalHeatingBTU =
    typeof heating === 'number' && Number.isFinite(heating) && heating > 0
      ? heating
      : null;

  const desiredCooling = Number(
    project.projectSettings.find((ps) => ps.code === 'desired_cooling')?.value
  );
  const returnAirTemperatureCooling =
    Number.isFinite(desiredCooling) && desiredCooling > 0
      ? desiredCooling
      : DEFAULT_RETURN_AIR_TEMPERATURE_COOLING;

  const desiredHeating = Number(
    project.projectSettings.find((ps) => ps.code === 'desired_heating')?.value
  );
  const returnAirTemperatureHeating =
    Number.isFinite(desiredHeating) && desiredHeating > 0
      ? desiredHeating
      : DEFAULT_RETURN_AIR_TEMPERATURE_HEATING;

  return {
    zipCode,
    totalCoolingBTU,
    returnAirTemperatureCooling,
    totalHeatingBTU,
    returnAirTemperatureHeating
  };
}

export function ductedCoolingOperatingCostRequestFields(
  params: DuctedCoolingOperatingCostParams
): {
  zipCode?: string;
  totalCoolingBTU?: number;
  returnAirTemperatureCooling?: number;
  totalHeatingBTU?: number;
  returnAirTemperatureHeating?: number;
} {
  // The zip alone is enough to ask for an estimate now — a project with no
  // block load still gets one, off the load the server resolves from the
  // tonnage seed. Only a project with no location has nothing to ask with.
  if (!params.zipCode) {
    return {};
  }
  return {
    zipCode: params.zipCode,
    returnAirTemperatureCooling: params.returnAirTemperatureCooling,
    returnAirTemperatureHeating: params.returnAirTemperatureHeating,
    ...(params.totalCoolingBTU != null
      ? { totalCoolingBTU: params.totalCoolingBTU }
      : {}),
    ...(params.totalHeatingBTU != null
      ? { totalHeatingBTU: params.totalHeatingBTU }
      : {})
  };
}
